import { ChangeDetectorRef, Component, inject, Input, OnDestroy, OnInit, signal, ViewChild } from '@angular/core';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { Observable, Subject, forkJoin } from 'rxjs';
import { debounceTime, distinctUntilChanged, skip, takeUntil } from 'rxjs/operators';
import { AbstractControl, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';

import { CompanyRolesAndPermissionComponent } from '../../../../shared/components/company-roles-and-permissions/company-roles-and-permissions.component';
import { NewWorkspaceUserComponent } from '../new-user/new-workspace-user.component';
import { UploadModalComponent } from '../../../../on-boarding/profile-avatar/upload-modal/upload-modal.component';
import { UploadFileComponent } from '../../../../shared/components/mtm-upload-file/upload-file.component';

import { TranslatePipe } from '../../../../shared/pipes/translate.pipe';
import { EmitterService } from '../../../../shared/services/emitter.service';
import { OverlayService } from '../../../../shared/services/overlayService';
import { activeLanguageUpdated } from '../../../../shared/services/translate.service';
import { UserService } from '../../../../shared/services/user.service';
import { WorkspaceService } from '../../../../shared/services/workspace.service';
import { TimelineService } from '../../../../shared/services/timeline.service';
import { AuthService } from '../../../../shared/services/auth.service';
import { GoogleAnalyticsService } from '../../../../shared/services/google-analytics.service';
import { MtmBreadcrumbService } from '../../../../shared/services/mtm-breadcrumb.service';
import { noHtmlValidator } from '../../../../shared/validators/nohtml.validator';

import { WorkspacePermissionsImgUploadService } from './services/workspace-permissions-img-upload.service';
import { WorkspacePermissionHelperService } from './services/workspace-permissions-helper.service';
import { StatusInfo, User, UserFormWrapper, Workspace, WorkspaceOnSaveEvent } from '../../../../shared/interfaces';
import { AvatarImageResult, SaveWorkspaceResult, UserWorkspaceRole } from '../../../models/workspace.model';
import { WithPendingGuard } from '../../../../shared/decorators';

import { WORKSPACE_KEYS, WORKSPACE_MODAL_CONFIG, TRANSLATION_KEYS, UI } from '../../constants/workspace.constants';
import { LogService } from '../../../../shared/services/log.service';

@Component({
  selector: 'mtm-edit-workspace-permissions',
  templateUrl: './workspace-permissions.component.html',
  styleUrl: './workspace-permissions.component.scss',
  standalone: false,
})
export class EditWorkspacePermissionsComponent implements OnInit, OnDestroy {
  @ViewChild('avatarUploader', { static: false }) avatarUploader: UploadFileComponent;
  @ViewChild('coverUploader', { static: false }) coverUploader: UploadFileComponent;
  @Input() setBreadcrumb: () => void;
  @Input() owner: string;

  private _workspaceId: string = '';
  private newModalRef: NgbModalRef;
  private uploadModal: NgbModalRef;
  private readonly ngUnsubscribe: Subject<void> = new Subject<void>();

  companyUsersList: User[] = [];
  companyUsers: User[] = [];

  users: UserWorkspaceRole[] = [];
  private readonly filteredUsersSignal = signal<UserWorkspaceRole[]>([]);
  private readonly firstNameFilterSignal = signal<string>('');
  private readonly lastNameFilterSignal = signal<string>('');
  private readonly emailFilterSignal = signal<string>('');
  private readonly nameValueSignal = signal<string>('');
  private readonly isPendingSignal = signal<boolean>(false);
  private readonly submittedSignal = signal<boolean>(false);

  get filteredUsers(): UserWorkspaceRole[] {
    return this.filteredUsersSignal();
  }
  set filteredUsers(value: UserWorkspaceRole[]) {
    this.filteredUsersSignal.set(value ?? []);
  }

  get firstNameFilter(): string {
    return this.firstNameFilterSignal();
  }
  set firstNameFilter(value: string) {
    this.firstNameFilterSignal.set(value ?? '');
  }

  get lastNameFilter(): string {
    return this.lastNameFilterSignal();
  }
  set lastNameFilter(value: string) {
    this.lastNameFilterSignal.set(value ?? '');
  }

  get emailFilter(): string {
    return this.emailFilterSignal();
  }
  set emailFilter(value: string) {
    this.emailFilterSignal.set(value ?? '');
  }

  sortedColumn: string = '';
  sortAscending: boolean = true;
  rowFiltered: boolean = false;
  get isPending(): boolean {
    return this.isPendingSignal();
  }
  set isPending(value: boolean) {
    this.isPendingSignal.set(!!value);
  }
  _workspace: Workspace = null;
  workspaceName: string;
  workspaces: Workspace[] = null;
  cacheBuster: string = Date.now().toString();
  get nameValue(): string {
    return this.nameValueSignal();
  }
  set nameValue(value: string) {
    this.nameValueSignal.set(value ?? '');
  }
  avatarUrl: string = '';
  coverUrl: string = '';
  get submitted(): boolean {
    return this.submittedSignal();
  }
  set submitted(value: boolean) {
    this.submittedSignal.set(!!value);
  }
  headerForm: UntypedFormGroup = null;

  activationStatuses: StatusInfo[] = [
    { code: WORKSPACE_KEYS.ACTIVE_COMPLETED, label: WORKSPACE_KEYS.ACTIVE_COMPLETED_LABEL },
    { code: WORKSPACE_KEYS.PENDING_INPROGRESS, label: WORKSPACE_KEYS.PENDING_INPROGRESS_LABEL },
  ];

  timelineService: TimelineService = inject(TimelineService);

  constructor(
    private readonly authService: AuthService,
    private readonly workspaceService: WorkspaceService,
    private readonly overlayService: OverlayService,
    private readonly modalService: NgbModal,
    private readonly translatePipe: TranslatePipe,
    private readonly fb: UntypedFormBuilder,
    private readonly cd: ChangeDetectorRef,
    private readonly mtmBreadcrumbService: MtmBreadcrumbService,
    private readonly googleAnalyticsService: GoogleAnalyticsService,
    private readonly userService: UserService,
    private readonly workspacePermissionsImgUpload: WorkspacePermissionsImgUploadService,
    private readonly workspacePermissionHelper: WorkspacePermissionHelperService,
    private readonly logService: LogService
  ) {}

  get workspaceId(): string {
    return this._workspaceId;
  }

  get workspace(): Workspace {
    return this._workspace;
  }

  @Input()
  set workspaceId(value: string) {
    if (value) {
      this._workspaceId = value;
      this.loadWorkspace();
    }
  }

  ngOnInit(): void {
    this.googleAnalyticsService.sendUserAnalyticsConfig('', '', '');
    this.cacheBuster = Date.now().toString();
    this.initForm();
    this.setupAutoSave();
    this.setupLanguageListener();
    this.setupBreadcrumb();
    this.setupWorkspaceSaveListener();
  }

  ngOnDestroy(): void {
    this.mtmBreadcrumbService.setLinks([]);
    this.ngUnsubscribe.next(undefined);
    this.ngUnsubscribe.complete();
  }

  private setupBreadcrumb(): void {
    if (this.setBreadcrumb) {
      this.setBreadcrumb();
    }
  }

  private setupLanguageListener(): void {
    activeLanguageUpdated.pipe(takeUntil(this.ngUnsubscribe)).subscribe(() => this.cd.markForCheck());
  }

  private setupWorkspaceSaveListener(): void {
    EmitterService.get(WorkspaceOnSaveEvent)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(() => {
        this.saveWorkspace().catch((err: any) => {
          this.logService.logError(err, {
            message: TRANSLATION_KEYS.WORKSPACE_EDIT_ERROR,
            workspaceId: this._workspaceId,
          });
        });
      });
  }

  private setupAutoSave(): void {
    this.headerForm
      .get(WORKSPACE_KEYS.NAME)
      .valueChanges.pipe(
        debounceTime(WORKSPACE_KEYS.AUTOSAVE_DEBOUNCE),
        skip(1),
        distinctUntilChanged(),
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe(() => {
        if (!this.headerForm.invalid) {
          this.saveWorkspace().catch((err: any) =>
            this.logService.logError(err, {
              message: TRANSLATION_KEYS.WORKSPACE_EDIT_ERROR,
              workspaceId: this._workspaceId,
            })
          );
        }
      });
  }

  private initForm(): void {
    this.headerForm = this.fb.group({
      [WORKSPACE_KEYS.NAME]: [this.workspaceName || '', [Validators.required, noHtmlValidator]],
    });
  }

  private loadWorkspace(): void {
    const authUser: User = this.authService.getAuthUser();
    const workspace$: Observable<Workspace> = this.workspaceService.getWorkspaceById(
      authUser.companyId,
      this._workspaceId,
      true,
      {
        redirectOnForbidden: true,
      }
    );
    const users$: Observable<User[]> = this.userService.getUserByWorkspaceId(authUser.company.id, this._workspaceId);
    const companyUserData$: Observable<User[]> = this.userService.getUsersByCompanyId(authUser.company.id);

    forkJoin([workspace$, users$, companyUserData$])
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe({
        next: ([workspace, users, companyUserList]: [Workspace, User[], User[]]) => {
          this.setWorkspaceData(workspace, users, companyUserList);
        },
        error: (err: any) =>
          this.logService.logError(err, { message: WORKSPACE_KEYS.ERROR_FETCHING, workspaceId: this._workspaceId }),
      });
  }

  private setWorkspaceData(workspace: Workspace, users: User[], companyUserList: User[]): void {
    this.workspaces = [workspace];
    this._workspace = workspace;
    this.workspaceName = workspace.name;
    this.headerForm.patchValue({ [WORKSPACE_KEYS.NAME]: workspace.name });
    this.cacheWorkspaceImages();
    this.companyUsers = users;
    this.companyUsersList = companyUserList;
    this.initUsers();
    this.cd.detectChanges();
  }

  private cacheWorkspaceImages(): void {
    if (this._workspace.coverUrl) this._workspace.coverUrl += WORKSPACE_KEYS.CACHE_BUSTER_PARAM + this.cacheBuster;
    if (this._workspace.avatarUrl) this._workspace.avatarUrl += WORKSPACE_KEYS.CACHE_BUSTER_PARAM + this.cacheBuster;
  }

  public isValid(field: string): boolean {
    const control: AbstractControl = this.headerForm?.get(field);
    return (
      (control?.invalid || control?.hasError(WORKSPACE_KEYS.NO_HTML_VALIDATOR)) && (control?.touched || this.submitted)
    );
  }

  public removeLeadingSpaces(name: string): void {
    const control: AbstractControl = this.headerForm.get(name);
    if (control) {
      control.setValue(control.value.trimStart().replace(WORKSPACE_KEYS.INVALID_CHARS_REGEX, ''), { emitEvent: false });
    }
  }

  private initUsers(): void {
    if (!this._workspace) return;
    this.users = this.workspacePermissionHelper.initWorkspaceUsers({
      workspace: this._workspace,
      companyUsers: this.companyUsers,
      activationStatuses: this.activationStatuses,
      workspaces: this.workspaces,
    });
    this.filterUsers();
  }

  public filterUsers(): void {
    this.filteredUsers = [...this.users];
    this.rowFiltered = !!(this.emailFilter || this.firstNameFilter || this.lastNameFilter);

    if (this.rowFiltered) {
      this.filteredUsers = this.filteredUsers.filter(
        (user: UserWorkspaceRole) =>
          this.workspacePermissionHelper.matchesFilter(user.username, this.emailFilter, false) &&
          this.workspacePermissionHelper.matchesFilter(user.firstName, this.firstNameFilter, true) &&
          this.workspacePermissionHelper.matchesFilter(user.lastName, this.lastNameFilter, true)
      );
    }

    if (this.sortedColumn) {
      const multiplier: number = this.sortAscending ? 1 : -1;
      this.filteredUsers.sort(
        (a: UserWorkspaceRole, b: UserWorkspaceRole) =>
          (a[this.sortedColumn] || '').localeCompare(b[this.sortedColumn] || '') * multiplier
      );
    }
  }

  @WithPendingGuard
  public removeUser(user: User): void {
    if (!user) return;
    const index: number = this.users.findIndex((u: UserWorkspaceRole) => u.username === user.username);
    if (index === -1) return;

    const authUser: User = this.authService.getAuthUser();
    this.isPending = true;

    this.workspaceService
      .removeUserFromWorkspace(authUser.company.id, this._workspace.id, user.username)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe({
        next: () => {
          this.isPending = false;
          this.users.splice(index, 1);
          this.filterUsers();
        },
        error: () => {
          this.isPending = false;
          this.overlayService.showError(
            this.translatePipe.transform(TRANSLATION_KEYS.WORKSPACE_REMOVE_USER_FAILED),
            UI.HEADER_ERROR_TITLE
          );
        },
      });
  }

  public showUserModal(): void {
    this.newModalRef = this.modalService.open(NewWorkspaceUserComponent, {
      size: WORKSPACE_MODAL_CONFIG.SIZE,
      backdrop: 'static',
      keyboard: WORKSPACE_MODAL_CONFIG.KEYBOARD,
    });
    const instance: NewWorkspaceUserComponent = this.newModalRef.componentInstance;
    instance.companyUsers = this.companyUsersList;
    instance.currentUsers = [...this.users];
    instance.workspaces = this.workspaces;

    this.newModalRef.result.then((result: UserFormWrapper) => this.addNewResult(result)).catch(() => {});
  }

  private addNewResult(result: UserFormWrapper): void {
    const { username }: { username: string } = result.formData;
    if (this.users.some((u: UserWorkspaceRole) => u.username === username)) return;

    this.companyUsers.push(result.userData);

    this.workspacePermissionHelper
      .addUserToWorkspace({
        formData: result.formData,
        userData: result.userData,
        users: this.users,
        companyUsers: this.companyUsers,
        activationStatuses: this.activationStatuses,
        workspaceId: this._workspace.id,
        workspaces: this.workspaces,
        ngUnsubscribe: this.ngUnsubscribe,
      })
      .then((updatedUsers: UserWorkspaceRole[]) => {
        this.users = updatedUsers;
        this.filterUsers();
      });
  }

  private async saveWorkspace(): Promise<void> {
    const result: SaveWorkspaceResult = await this.workspacePermissionHelper.saveWorkspace({
      workspace: this._workspace,
      form: this.headerForm,
      trimString: this.trimString.bind(this),
      ngUnsubscribe: this.ngUnsubscribe,
      isPending: this.isPending,
    });

    if (result.success && result.updatedWorkspace) this._workspace = result.updatedWorkspace;
  }

  public save(): void {
    this.workspacePermissionHelper
      .saveUsersToWorkspace(this.users, this._workspace.id, this.ngUnsubscribe)
      .subscribe(() =>
        this.overlayService.showSuccess(
          this.translatePipe.transform(TRANSLATION_KEYS.OVERLAY_CHANGE_SAVE),
          UI.HEADER_SUCCESS_TITLE
        )
      );
  }

  public showPermissionDetailsInfo(): void {
    this.modalService.open(CompanyRolesAndPermissionComponent, {
      backdrop: true,
      windowClass: WORKSPACE_MODAL_CONFIG.PERMISSION_MODAL_CLASS,
    });
  }

  public openModal(): void {
    this.uploadModal = this.modalService.open(UploadModalComponent, { size: WORKSPACE_MODAL_CONFIG.SIZE });
    this.uploadModal.componentInstance.resizeToWidth = WORKSPACE_MODAL_CONFIG.AVATAR_WIDTH;
    this.uploadModal.componentInstance.aspectRatio = WORKSPACE_MODAL_CONFIG.AVATAR_ASPECT_RATIO;
    this.uploadModal.componentInstance.maintainAspectRatio = true;
  }

  public editAvatar(): void {
    this.avatarUploader.upload();
  }

  public fileChangeListener($event: any, imageType: 'avatar' | 'cover' = 'avatar'): void {
    const file = $event.files[0];
    if (!file) return;

    if (imageType === WORKSPACE_KEYS.AVATAR) {
      this.openModal();
      this.uploadModal.componentInstance.imageFile = file;
    }

    const reader: FileReader = new FileReader();
    reader.onloadend = (e: any) =>
      imageType === WORKSPACE_KEYS.COVER ? this.setCoverImage(e.target.result) : this.setAvatarImageOnModal();
    reader.readAsDataURL(file);
  }

  private setCoverImage(result: string): void {
    this.workspacePermissionsImgUpload
      .setCoverImage({
        result,
        workspace: this._workspace,
        cacheBuster: this.cacheBuster,
        ngUnsubscribe: this.ngUnsubscribe,
      })
      .then((updatedCoverUrl: string) => {
        this.coverUrl = updatedCoverUrl;
        this._workspace.coverUrl = updatedCoverUrl;
      });
  }

  private setAvatarImageOnModal(): void {
    this.workspacePermissionsImgUpload
      .setAvatarImageOnModal({
        uploadModal: this.uploadModal,
        cacheBuster: this.cacheBuster,
        workspace: this._workspace,
        ngUnsubscribe: this.ngUnsubscribe,
      })
      .then((result: AvatarImageResult) => {
        this.avatarUrl = result?.avatarUrl;
        this._workspace.avatarUrl = result?.workspaceAvatarUrl;
      });
  }

  public checkOwner(userEmail: string): boolean {
    return this._workspace.createdBy !== userEmail;
  }

  public onImageError(): void {
    if (this._workspace?.avatarUrl) this._workspace.avatarUrl = null;
    else if (this._workspace?.coverUrl) this._workspace.coverUrl = null;
  }

  private trimString(value: string): string {
    return value ? value.trim() : '';
  }
}
